import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { ConfirmDialog } from "../components/ui";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";

type UserRow = { id:string; email:string; role:string; is_active:boolean; first_name:string|null; last_name:string|null; created_at:string|null; last_login_at:string|null };
const ROLES = ["admin","analyst","readonly"];
const inp = (extra?:React.CSSProperties):React.CSSProperties => ({ background:"var(--clr-input-bg)", border:"1px solid var(--clr-border)", borderRadius:12, color:"var(--clr-text)", padding:"7px 12px", fontSize:"0.82rem", outline:"none", width:"100%", boxSizing:"border-box" as const, ...extra });

const EMPTY_CREATE = { email:"", password:"", role:"readonly", first_name:"", last_name:"" };

const smallBtn = (extra?:React.CSSProperties):React.CSSProperties => ({ background:"var(--clr-surface)", color:"var(--clr-text2)", border:"1px solid var(--clr-border)", borderRadius:999, padding:"3px 12px", fontSize:"0.75rem", fontWeight:500, cursor:"pointer", ...extra });

export default function AdminPage() {
  const t = useT();
  const fmt = useFmt();
  const G = useAuthStore(s => s.user?.primary_color) || "#00843D";
  // Badges de ROL: excepción v3 — conservan fondo tintado suave (etiqueta funcional).
  const ROLE_COLORS:Record<string,string> = { admin:"#f59e0b", analyst:G, readonly:"#64748b" };
  const [users, setUsers]       = useState<UserRow[]>([]);
  const [createModal, setCreateModal] = useState(false);
  const [editUser, setEditUser] = useState<UserRow|null>(null);
  const [form, setForm]         = useState(EMPTY_CREATE);
  const [editForm, setEditForm] = useState({ first_name:"", last_name:"", email:"", role:"readonly", password:"" });
  const [msg, setMsg]           = useState("");
  const [deleteId, setDeleteId] = useState<string|null>(null);
  const me = useAuthStore(s=>s.user?.email);
  // Alta/edición/baja de usuarios es superadmin-only desde v2.7 (org admins: solo lectura).
  // Impersonando se actúa con el token del usuario org → también solo lectura;
  // el superadmin gestiona desde /superadmin/usuarios.
  const canManage = useAuthStore(s=>s.user?.role) === "superadmin";
  const { isMobile } = useBreakpoint();

  const load = () => api.get<UserRow[]>("/users").then(setUsers).catch(console.error);
  useEffect(()=>{ load(); },[]);

  const flash = (text:string) => { setMsg(text); setTimeout(()=>setMsg(""),3000); };

  /* ── Create ── */
  const createUser = async (e:React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      setCreateModal(false); setForm(EMPTY_CREATE);
      load(); flash(t("admin.userCreated"));
    } catch(e:any) { flash(t("admin.errorPrefix")+e.message); }
  };

  /* ── Edit ── */
  const openEdit = (u:UserRow) => {
    setEditUser(u);
    setEditForm({ first_name:u.first_name??"", last_name:u.last_name??"", email:u.email, role:u.role, password:"" });
  };

  const saveEdit = async (e:React.FormEvent) => {
    e.preventDefault();
    if(!editUser) return;
    try {
      const body: Record<string,string> = {
        first_name: editForm.first_name,
        last_name:  editForm.last_name,
        role:       editForm.role,
      };
      if(editForm.password) body.password = editForm.password;
      await api.put(`/users/${editUser.id}`, body);
      setEditUser(null); load(); flash(t("admin.userUpdated"));
    } catch(e:any) { flash(t("admin.errorPrefix")+e.message); }
  };

  /* ── Other actions ── */
  const toggleActive = async (u:UserRow) => { await api.put(`/users/${u.id}`, { is_active:!u.is_active }); load(); };
  const deleteUser   = async (id:string) => {
    setDeleteId(null);
    try { await api.delete(`/users/${id}`); load(); flash(t("admin.userDeleted")); }
    catch(e:any) { flash(t("admin.errorPrefix")+e.message); }
  };

  return (
    <div className="page" style={{ color:"var(--clr-text)", fontVariantNumeric:"tabular-nums" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <h1 style={{ margin:0, fontSize:"1.4rem", fontWeight:700, letterSpacing:"-0.02em" }}>{t("admin.title")}</h1>
        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
          {msg&&<span style={{ color:msg.startsWith("Error")?"#f87171":"var(--clr-text3)", fontSize:"0.82rem", fontWeight:500 }}>{msg}</span>}
          {canManage&&<button onClick={()=>setCreateModal(true)} style={{ background:"var(--clr-org-primary)", color:"#fff", border:0, borderRadius:999, padding:"0.48rem 1.2rem", fontWeight:600, cursor:"pointer", fontSize:"0.82rem" }}>{t("admin.newUser")}</button>}
        </div>
      </div>

      {!canManage&&(
        <div style={{ background:"var(--clr-surface)", border:"1px solid var(--clr-border)", borderRadius:12, backdropFilter:"var(--clr-blur)", WebkitBackdropFilter:"var(--clr-blur)", boxShadow:"var(--clr-shadow-sm)", padding:"10px 16px", marginBottom:16, fontSize:"0.82rem", color:"var(--clr-text2)" }}>
          {t("admin.readonlyNoticePre")}<a href="mailto:luis@haltrixpartners.com" style={{ color:"var(--clr-org-primary)", fontWeight:600 }}>luis@haltrixpartners.com</a>{t("admin.readonlyNoticePost")}
        </div>
      )}

      <div style={{ background:"var(--clr-surface)", border:"1px solid var(--clr-border)", borderRadius:18, overflow:"hidden", backdropFilter:"var(--clr-blur)", WebkitBackdropFilter:"var(--clr-blur)", boxShadow:"var(--clr-shadow-sm)" }}>
        <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.82rem" }}>
          <thead>
            <tr style={{ textAlign:"left", borderBottom:"1px solid var(--clr-border2)" }}>
              {[t("admin.colName"),t("admin.colEmail"),t("admin.colRole"),t("admin.colStatus"),...(isMobile?[]:[t("admin.colCreated"),t("admin.colLastLogin")]),...(canManage?[t("admin.colActions")]:[])].map(h=><th key={h} style={{ padding:"10px 14px", fontSize:"0.65rem", fontWeight:600, textTransform:"uppercase", letterSpacing:0.9, color:"var(--clr-text4)", whiteSpace:"nowrap" }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users.map(u=>(
              <tr key={u.id} style={{ borderTop:"1px solid var(--clr-border2)", opacity:u.is_active?1:0.5 }}>
                <td style={{ padding:"10px 14px", fontWeight:600, color:"var(--clr-text)" }}>
                  {u.first_name||u.last_name ? `${u.first_name??""} ${u.last_name??""}`.trim() : <span style={{ color:"var(--clr-text4)", fontWeight:400 }}>–</span>}
                  {u.email===me&&<span style={{ fontSize:"0.68rem", color:"var(--clr-org-primary)", fontWeight:600, marginLeft:6 }}>{t("admin.you")}</span>}
                </td>
                <td style={{ padding:"10px 14px", color:"var(--clr-text2)", fontSize:"0.8rem" }}>{u.email}</td>
                <td style={{ padding:"10px 14px" }}>
                  <span style={{ background:ROLE_COLORS[u.role]+"22", color:ROLE_COLORS[u.role], padding:"2px 10px", borderRadius:999, fontSize:"0.72rem", fontWeight:600 }}>{u.role}</span>
                </td>
                <td style={{ padding:"10px 14px" }}>
                  {(() => { const sc = u.is_active?"#16a34a":"#64748b"; return (
                    <span style={{ display:"inline-flex", alignItems:"center", gap:6, background:sc+"1e", color:sc, borderRadius:999, padding:"3px 11px", fontSize:"0.72rem", fontWeight:600, whiteSpace:"nowrap" }}>
                      {u.is_active?t("admin.active"):t("admin.inactive")}
                    </span>
                  ); })()}
                </td>
                {!isMobile && <td style={{ padding:"10px 14px", color:"var(--clr-text3)", fontSize:"0.75rem" }}>{u.created_at?fmt.date(u.created_at):"–"}</td>}
                {!isMobile && <td style={{ padding:"10px 14px", color:"var(--clr-text3)", fontSize:"0.75rem" }}>{u.last_login_at?fmt.dateTime(u.last_login_at):t("admin.never")}</td>}
                {canManage&&<td style={{ padding:"10px 14px" }}>
                  {u.email!==me&&(
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={()=>openEdit(u)} style={smallBtn({ fontWeight:600 })}>{t("admin.edit")}</button>
                      <button onClick={()=>toggleActive(u)} style={smallBtn()}>
                        {u.is_active?t("admin.deactivate"):t("admin.activate")}
                      </button>
                      <button onClick={()=>setDeleteId(u.id)} style={smallBtn({ color:"#ef4444" })}>{t("admin.delete")}</button>
                    </div>
                  )}
                </td>}
              </tr>
            ))}
            {users.length===0&&<tr><td colSpan={(isMobile?4:6)+(canManage?1:0)} style={{ padding:32, textAlign:"center", color:"var(--clr-text4)" }}>{t("admin.noUsers")}</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      <ConfirmDialog
        open={deleteId!==null}
        title={t("admin.confirmDeleteTitle")}
        message={t("admin.confirmDeleteMsg")}
        onConfirm={()=>deleteId&&deleteUser(deleteId)}
        onCancel={()=>setDeleteId(null)}
      />

      {/* ── Modal crear ── */}
      {createModal&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(11,18,21,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:999, backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)" }}>
          <form onSubmit={createUser} style={{ background:"var(--clr-modal-solid)", border:"1px solid var(--clr-border)", borderRadius:22, padding:"1.75rem", width:400, boxShadow:"0 24px 80px -24px rgba(0,0,0,0.45)" }}>
            <h3 style={{ margin:"0 0 1.25rem", color:"var(--clr-text)", letterSpacing:"-0.01em" }}>{t("admin.modalCreateTitle")}</h3>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
              <label style={{ display:"block" }}>
                <span style={{ fontSize:"0.72rem", color:"var(--clr-text3)", display:"block", marginBottom:4 }}>{t("admin.fieldFirstName")}</span>
                <input value={form.first_name} onChange={e=>setForm(f=>({...f,first_name:e.target.value}))} style={inp()} placeholder="Ana" />
              </label>
              <label style={{ display:"block" }}>
                <span style={{ fontSize:"0.72rem", color:"var(--clr-text3)", display:"block", marginBottom:4 }}>{t("admin.fieldLastName")}</span>
                <input value={form.last_name} onChange={e=>setForm(f=>({...f,last_name:e.target.value}))} style={inp()} placeholder="García" />
              </label>
            </div>
            <label style={{ display:"block", marginBottom:12 }}>
              <span style={{ fontSize:"0.72rem", color:"var(--clr-text3)", display:"block", marginBottom:4 }}>{t("admin.fieldEmail")}</span>
              <input type="email" required value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} style={inp()} />
            </label>
            <label style={{ display:"block", marginBottom:12 }}>
              <span style={{ fontSize:"0.72rem", color:"var(--clr-text3)", display:"block", marginBottom:4 }}>{t("admin.fieldPassword")}</span>
              <input type="password" required minLength={8} value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} style={inp()} />
            </label>
            <label style={{ display:"block", marginBottom:20 }}>
              <span style={{ fontSize:"0.72rem", color:"var(--clr-text3)", display:"block", marginBottom:4 }}>{t("admin.fieldRole")}</span>
              <select value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} style={inp()}>
                {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button type="button" onClick={()=>setCreateModal(false)} style={{ background:"var(--clr-modal-solid2)", color:"var(--clr-text2)", border:"1px solid var(--clr-border2)", borderRadius:999, padding:"0.5rem 1.3rem", fontSize:"0.8rem", fontWeight:600, cursor:"pointer" }}>{t("admin.cancel")}</button>
              <button type="submit" style={{ background:"var(--clr-org-primary)", color:"#fff", border:0, borderRadius:999, padding:"0.5rem 1.3rem", fontSize:"0.8rem", fontWeight:600, cursor:"pointer" }}>{t("admin.createUser")}</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Modal editar ── */}
      {editUser&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(11,18,21,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:999, backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)" }}>
          <form onSubmit={saveEdit} style={{ background:"var(--clr-modal-solid)", border:"1px solid var(--clr-border)", borderRadius:22, padding:"1.75rem", width:400, boxShadow:"0 24px 80px -24px rgba(0,0,0,0.45)" }}>
            <h3 style={{ margin:"0 0 4px", color:"var(--clr-text)", letterSpacing:"-0.01em" }}>{t("admin.modalEditTitle")}</h3>
            <div style={{ fontSize:"0.78rem", color:"var(--clr-text3)", marginBottom:"1.25rem" }}>{editUser.email}</div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
              <label style={{ display:"block" }}>
                <span style={{ fontSize:"0.72rem", color:"var(--clr-text3)", display:"block", marginBottom:4 }}>{t("admin.fieldFirstName")}</span>
                <input value={editForm.first_name} onChange={e=>setEditForm(f=>({...f,first_name:e.target.value}))} style={inp()} placeholder="Ana" />
              </label>
              <label style={{ display:"block" }}>
                <span style={{ fontSize:"0.72rem", color:"var(--clr-text3)", display:"block", marginBottom:4 }}>{t("admin.fieldLastName")}</span>
                <input value={editForm.last_name} onChange={e=>setEditForm(f=>({...f,last_name:e.target.value}))} style={inp()} placeholder="García" />
              </label>
            </div>
            <label style={{ display:"block", marginBottom:12 }}>
              <span style={{ fontSize:"0.72rem", color:"var(--clr-text3)", display:"block", marginBottom:4 }}>{t("admin.fieldRole")}</span>
              <select value={editForm.role} onChange={e=>setEditForm(f=>({...f,role:e.target.value}))} style={inp()}>
                {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label style={{ display:"block", marginBottom:20 }}>
              <span style={{ fontSize:"0.72rem", color:"var(--clr-text3)", display:"block", marginBottom:4 }}>{t("admin.fieldNewPassword")} <span style={{ fontWeight:400 }}>{t("admin.fieldNewPasswordHint")}</span></span>
              <input type="password" minLength={8} value={editForm.password} onChange={e=>setEditForm(f=>({...f,password:e.target.value}))} style={inp()} placeholder="••••••••" />
            </label>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button type="button" onClick={()=>setEditUser(null)} style={{ background:"var(--clr-modal-solid2)", color:"var(--clr-text2)", border:"1px solid var(--clr-border2)", borderRadius:999, padding:"0.5rem 1.3rem", fontSize:"0.8rem", fontWeight:600, cursor:"pointer" }}>{t("admin.cancel")}</button>
              <button type="submit" style={{ background:"var(--clr-org-primary)", color:"#fff", border:0, borderRadius:999, padding:"0.5rem 1.3rem", fontSize:"0.8rem", fontWeight:600, cursor:"pointer" }}>{t("admin.saveChanges")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
